import type { LucideIconName } from "../../FG/LucideIcon";
import { CADENCE_QUESTION } from "../../../lib/questionCopy";

/**
 * D-16 (architecture C) — the wizard's step metadata + path orders, one
 * decision per screen. Step ids are named strings (decoupled from display
 * order, so reorders never renumber anything):
 *
 *   q1 Race question · race · method · goal · length · fuel (fueling
 *   experience) · body · needs (needs & tastes) · cadence (box cadence) · review
 */
export type WizardStepId =
  | "q1"
  | "race"
  | "method"
  | "goal"
  | "length"
  | "fuel"
  | "body"
  | "needs"
  | "cadence"
  | "review";

export type WizardStepMeta = {
  label: string;
  icon: LucideIconName;
  title: string;
  subtitle: string | null;
};

export const STEP_META: Record<WizardStepId, WizardStepMeta> = {
  q1: {
    label: "Start",
    icon: "sparkles",
    title: "Do you have a race coming up?",
    subtitle: "This shapes your setup — you can add or change a race anytime."
  },
  race: {
    label: "Race",
    icon: "flag",
    title: "Let's get started! Which race are you training for?",
    subtitle: "We'll build your fueling plan around this event — training, carbs, race-day fuel."
  },
  method: {
    label: "Training",
    icon: "sparkles",
    title: "Do you have a training plan?",
    subtitle: "Pick the one that fits — you can change it later."
  },
  goal: {
    label: "Goal",
    icon: "target",
    title: "Set your goal",
    subtitle: "Not sure? Pick a time you could hold today — you can change it anytime."
  },
  length: {
    label: "Plan",
    icon: "calendar",
    title: "How long a plan?",
    subtitle: "Both plans end on race day — you're choosing how long the build-up before it is."
  },
  fuel: {
    label: "Fuel",
    icon: "cup",
    title: "Your fueling experience",
    subtitle: null
  },
  body: {
    label: "You",
    icon: "user",
    title: "About your body",
    subtitle: "Sex, weight and sweat directly size your carbs and sodium."
  },
  needs: {
    label: "Needs",
    icon: "heart",
    title: "Needs & tastes",
    subtitle:
      "Allergens are locked in — we never cross them. Flavors help us pick products you'll actually enjoy."
  },
  cadence: {
    label: "Boxes",
    icon: "package",
    title: CADENCE_QUESTION.title,
    subtitle: null
  },
  review: {
    // Title is dynamic (`Your {N}-week journey` / build / no-race variants) —
    // the orchestrator overrides it; this base is never rendered.
    label: "Journey",
    icon: "package",
    title: "Your journey",
    subtitle: null
  }
};

/** Per-step footer footnotes (the review's variants stay in the orchestrator). */
export const STEP_FOOTNOTES: Partial<Record<WizardStepId, string>> = {
  q1: "Free · no account needed · about 5 minutes.",
  fuel: "Tunes the fueling plan we build — update anytime from your preferences.",
  body: "Optional, but they make your targets meaningfully more accurate.",
  needs: "Allergens are always respected; flavors guide our picks where possible.",
  cadence: "You review and buy each box as you go — nothing is charged automatically."
};

/**
 * The active path's ordered step ids — the ONE source of truth for navigation
 * and the "Step X of N" counter (D-16). Two paths, both opened by Q1:
 *   - race: the race, how they'll train, and (Fuel Goods plan only) goal + length;
 *   - no race (`hasRace === false`): how they plan training, then a rolling journey.
 * `hasRace` is null until Q1 is answered; the counter then shows the race path.
 * `skipMethod` (D18 — the athlete already has upcoming workouts) drops the
 * training-plan question on both paths. Pure so the orders are unit-testable.
 */
export function wizardStepOrder({
  hasRace,
  isPlan,
  skipMethod = false
}: {
  hasRace: boolean | null;
  isPlan: boolean;
  skipMethod?: boolean;
}): WizardStepId[] {
  const profile: WizardStepId[] = ["fuel", "body", "needs", "cadence", "review"];
  const order: WizardStepId[] =
    hasRace === false
      ? ["q1", "method", ...profile]
      : isPlan
        ? ["q1", "race", "method", "goal", "length", ...profile]
        : ["q1", "race", "method", ...profile];
  return skipMethod ? order.filter((id) => id !== "method") : order;
}
